<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { onMounted, ref, watch } from 'vue'
import { faMinus, faPlus } from '@fortawesome/duotone-regular-svg-icons'
import Button from '~/components/ui/Button.vue'
import AuCheckbox from '~/components/ui/AuCheckbox.vue'

const props = defineProps<{
  isRefetching: boolean
  subjects: string[]
  selectedSubjects: string[]
  title?: string
}>()

const emit = defineEmits(['toggleCriterion'])

const { t } = useI18n()

const open = ref(false)

onMounted(() => {
  if (!props.selectedSubjects.length)
    open.value = true
})

watch(() => props.selectedSubjects.length, () => open.value = !open.value)
</script>

<template>
  <div class="rounded-xl bg-white">
    <button
      class="group flex w-full cursor-pointer items-center justify-between rounded px-4 py-3 focus-visible:ring"
      @click="open = !open"
    >
      <span
        class="font-bold underline-offset-4 group-hover:underline"
        v-text="title"
      />
      <font-awesome-icon
        :icon="open ? faMinus : faPlus"
        :spin="open"
        style="--fa-animation-duration: 200ms; --fa-animation-iteration-count: 1;"
      />
    </button>
    <Transition
      :duration="500"
      name="nested"
    >
      <div
        v-show="open"
        class="outer border-t-gray-10 mx-4 border-t py-3"
      >
        <ul class="inner flex flex-col gap-2">
          <li
            v-for="(subject, i) in subjects"
            :key="i"
          >
            <Button
              type="label"
              full-width
              variant="ghost"
              size="md"
            >
              <AuCheckbox
                :id="`subject-${subject}`"
                :model-value="selectedSubjects.includes(subject)"
                @update:model-value="() => emit('toggleCriterion', subject)"
              />
              <span v-text="t(`metadata.subjects.${subject}`)" />
            </Button>
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.nested-enter-active,
.nested-leave-active {
  transition: all 0.3s linear;
  max-height: 500px;
}
/* delay leave of parent element */
.nested-leave-active {
  transition-delay: 0.2s;
}

.nested-enter-from,
.nested-leave-to {
  transform: translateY(-5px);
  opacity: 0;
  max-height: 0;
}

.nested-enter-active .inner,
.nested-leave-active .inner {
  transition:
    transform 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
  will-change: transform, opacity;
}
/* delay enter of nested element */
.nested-enter-active .inner {
  transition-delay: 0.2s;
}

.nested-enter-from .inner {
  transform: translateY(-5px);
  opacity: 0.001;
}

.nested-leave-to .inner {
  transform: translateY(-5px);
  opacity: 0.001;
}
</style>
